/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { InputSwitch } from 'primereact/inputswitch';
import { MultiSelect } from 'primereact/multiselect';
import { InputTextarea } from 'primereact/inputtextarea';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import PageSkeleton from '../../components/common/page-skeleton';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import FormInput from '../../components/forms/form-input';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import { connectionTypesService } from '../../services/connection-types.service';
import { useValueSetOptions } from '../../hooks/useValueSetOptions';
import { useCanCreate, useCanUpdate } from '../../hooks/usePermissions';
import { extractErrorMessage } from '../../utils/error-handler';
import ScreenHeader from '../../components/common/screen-header';

const LIST = '/partners?tab=types';

const AUTH_TYPE_OPTIONS = [
  { label: 'Bearer / API token', value: 'BEARER' },
  { label: 'Basic', value: 'BASIC' },
  { label: 'OAuth2', value: 'OAUTH2' },
  { label: 'None', value: 'NONE' },
];

const EMPTY = {
  code: '', name: '', description: '',
  supportedAuthTypes: [] as string[], credentialSchema: '', isActive: true,
};

const ConnectionTypeForm = () => {
  const navigate = useAppNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.CONNECTION_TYPES);
  const canUpdate = useCanUpdate(JOB_NAMES.CONNECTION_TYPES);

  const [form, setForm] = useState({ ...EMPTY });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const setField = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  // Auth types — admin-editable via Value Sets, falling back to the built-in list.
  const authTypeOptions = useValueSetOptions('connectionAuthType', AUTH_TYPE_OPTIONS);

  const { data: existing, isLoading } = useQuery({
    queryKey: ['connection-types', id],
    queryFn: () => connectionTypesService.getById(id!),
    enabled: isEdit && !!id,
  });
  useBreadcrumbTitle(isEdit ? existing?.name : null);

  useEffect(() => {
    if (existing && isEdit) {
      setForm({
        code: existing.code || '',
        name: existing.name || '',
        description: existing.description || '',
        supportedAuthTypes: existing.supportedAuthTypes || [],
        credentialSchema: existing.credentialSchema ? JSON.stringify(existing.credentialSchema, null, 2) : '',
        isActive: existing.isActive ?? true,
      });
    }
  }, [existing, isEdit]);

  const notify = (severity: 'success' | 'error', detail: string) =>
    toast.current?.show({ severity, summary: severity === 'error' ? 'Error' : 'Success', detail, life: severity === 'error' ? 8000 : 3000 });

  const mutation = useMutation({
    mutationFn: async () => {
      const e: Record<string, string> = {};
      if (!form.name.trim()) e.name = 'Name is required';
      if (!isEdit && !form.code.trim()) e.code = 'Code is required';
      let credentialSchema: any = undefined;
      if (form.credentialSchema.trim()) {
        try { credentialSchema = JSON.parse(form.credentialSchema); }
        catch { e.credentialSchema = 'Must be valid JSON'; }
      }
      setErrors(e);
      if (Object.keys(e).length) throw new Error('validation');
      const base = {
        name: form.name.trim(), description: form.description,
        supportedAuthTypes: form.supportedAuthTypes, credentialSchema, isActive: form.isActive,
      };
      if (isEdit && id) await connectionTypesService.update(id, base as any);
      else await connectionTypesService.create({ code: form.code.trim().toUpperCase(), ...base } as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connection-types'] });
      notify('success', `Connection type ${isEdit ? 'updated' : 'created'} successfully.`);
      setTimeout(() => navigate(LIST), 700);
    },
    onError: (err: any) => {
      if (err?.message === 'validation') return; // field errors already shown
      notify('error', extractErrorMessage(err, `Failed to ${isEdit ? 'update' : 'create'} connection type`));
    },
  });

  const saving = mutation.isPending;
  const canSave = isEdit ? canUpdate : canCreate;

  if (isEdit && isLoading) return <PageSkeleton />;

  return (
    <PermissionGuard jobName={JOB_NAMES.CONNECTION_TYPES}>
      <Toast ref={toast} position="top-right" />
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900 min-h-screen" data-testid="SCR-ConnectionType-Form">
        <ScreenHeader icon="pi pi-link" title={isEdit ? 'Edit Connection Type' : 'Create Connection Type'} subtitle="Configurable catalogue of connection kinds" onBack={() => navigate(LIST)} />

        <form onSubmit={(ev) => { ev.preventDefault(); mutation.mutate(); }}>
          <div className="ds-card p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                name="code" label="Code" required
                value={form.code}
                onChange={(e) => { setField('code', e.target.value); setErrors((x) => ({ ...x, code: '' })); }}
                disabled={isEdit}
                placeholder="e.g. REST_API"
                error={!!errors.code}
                helperText={errors.code}
              />
              <FormInput
                name="name" label="Name" required
                value={form.name}
                onChange={(e) => { setField('name', e.target.value); setErrors((x) => ({ ...x, name: '' })); }}
                error={!!errors.name}
                helperText={errors.name}
              />

              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-ink">Supported Auth Types</label>
                <MultiSelect value={form.supportedAuthTypes} onChange={(e) => setField('supportedAuthTypes', e.value)}
                  options={authTypeOptions} optionLabel="label" optionValue="value" placeholder="Select" display="chip" className="w-full" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-ink">Active</label>
                <div className="pt-1"><InputSwitch checked={form.isActive} onChange={(e: any) => setField('isActive', e.value)} /></div>
              </div>

              <div className="md:col-span-2">
                <FormInput
                  name="description" label="Description"
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                />
              </div>

              <div className="md:col-span-2 flex flex-col gap-1">
                <label className="text-sm font-medium text-ink">Credential Schema (JSON)</label>
                <InputTextarea value={form.credentialSchema}
                  onChange={(e) => { setField('credentialSchema', e.target.value); setErrors((x) => ({ ...x, credentialSchema: '' })); }}
                  rows={8} className={`font-mono text-xs ${errors.credentialSchema ? 'p-invalid' : ''}`}
                  placeholder='{ "config": [...], "credentials": [...] }' />
                {errors.credentialSchema && <small className="text-red-600">{errors.credentialSchema}</small>}
              </div>
            </div>

            <div className="mt-4 px-4 py-3 -mx-4 -mb-4 bg-canvas dark:bg-gray-900/50 border-t border-line flex justify-end gap-2 rounded-b-lg">
              <Button label="Cancel" severity="secondary" outlined size="small" onClick={() => navigate(LIST)} disabled={saving} />
              {canSave && (
                <Button type="submit" label={saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                  icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={saving} size="small"
                  testId="ConnectionTypeForm.Button.Save" />
              )}
            </div>
          </div>
        </form>
      </div>
    </PermissionGuard>
  );
};

export default ConnectionTypeForm;
